/**
 * Resumable multipart upload.
 *
 * Ported from the previous implementation, which was the one piece worth keeping. Two defects
 * are corrected here without any change to the wire contract:
 *
 *   1. it presigned one part per request  -> now batched, up to PRESIGN_BATCH per call
 *   2. it uploaded parts strictly serially -> now bounded parallelism
 *
 * Both are hard requirements in docs/30-interfaces/frontend.md section 4. The server-supplied
 * chunk size, the declare-checksum-before-upload rule (INV-63) and the resume state that
 * survives navigation are all preserved from the original.
 */
import { api } from './api';

/** The API rejects more than 100 parts per presign call (apps/api/src/datasets.ts:32). */
const PRESIGN_BATCH = 100;

/**
 * How many parts move at once, smaller for a big file.
 *
 * This was a flat 4, under a comment claiming it was "enough to saturate a link without triggering
 * store-side throttling" — which is the thing that then happened, and only ever on large files. The
 * store said so itself, once the client was made to read the response body:
 *
 *   503 RequestTimeout — "a timeout occurred while trying to lock a resource, please reduce your
 *   request rate"
 *
 * The parts of one multipart upload all contend for the same object's lock, so what matters is how
 * many are in flight *against a single key*, not how many requests the store is serving overall.
 * That is why a 200 MB file failed where a 40 MB one never did. Fewer of them is the store's own
 * advice, and on a link this already saturates it costs close to nothing.
 */
const concurrencyFor = (bytes: number) => (bytes > 100 * 1024 * 1024 ? 2 : 4);

/**
 * A part is worth retrying when the answer says "not now" rather than "not this".
 *
 * The same boundary the rest of the app already draws: `providers.tsx` refuses to retry anything
 * below 500, and `qa-client.ts` treats `>= 500 || 429` as ambiguous. A 403 is an expired signature
 * and a 400 is a checksum the store disagrees with — repeating either only wastes the vendor's time.
 */
const RETRYABLE_STATUS = (status: number) => status >= 500 || status === 429;
const MAX_PART_ATTEMPTS = 5;
const BASE_BACKOFF_MS = 500;

interface InitiateResponse {
  dataset_file_id: string;
  s3_key: string;
  upload_id: string;
  chunk_size: number;
  total_parts: number;
}

interface CompletedPart {
  part_number: number;
  etag: string;
}

/** SHA-256 of a blob, base64 - the format the store expects for x-amz-checksum-sha256. */
export async function hashPart(blob: Blob): Promise<string> {
  const bytes = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return btoa(String.fromCharCode(...new Uint8Array(digest)));
}

/**
 * The whole file's digest — the identity a version delta is computed against.
 *
 * `hashPart` above hashes one 8 MiB slice for the store to verify in transit; part digests do not
 * compose into an object digest, so this is a second pass over the bytes. That cost buys the vendor
 * not re-uploading the three hundred files they did not change, which is the entire point of a
 * remediation being a delta rather than a fresh upload.
 *
 * `hashPart` is reused verbatim: the wire format is the same base64 SHA-256, and having two digest
 * encodings in one uploader is how a comparison silently stops matching.
 */
export const hashFile = (file: File) => hashPart(file);

/** Where a file sits inside the selection. This is what a delta compares, not the bare name. */
export const filePath = (file: File) =>
  (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;

export interface VersionPlan {
  package_version: number;
  /** Paths the folder already holds these exact bytes for — nothing to send. */
  have: string[];
  need: { path: string; sha: string }[];
  /** Present in the previous version, absent from this selection. */
  deletes: string[];
}

/**
 * Ask what actually has to be uploaded, then say what was dropped.
 *
 * Hashing happens here, before a single byte moves, because the answer decides whether any bytes
 * move at all.
 */
export async function planVersion(datasetId: string, files: File[]): Promise<VersionPlan> {
  const declared = await Promise.all(files.map(async file => ({ path: filePath(file), sha: await hashFile(file) })));
  /**
   * Whether this selection is the whole dataset or an addition to it.
   *
   * A folder pick carries `webkitRelativePath` on every file and says "this is the dataset" — so
   * anything the version holds that is not in it has been removed. Picking individual files says
   * "also take these", and reading that as a complete statement would delete everything the vendor
   * did not happen to re-select. Only the folder shape can safely imply deletions.
   */
  const replace = files.some(file => !!(file as File & { webkitRelativePath?: string }).webkitRelativePath);
  return api<VersionPlan>(`/datasets/${datasetId}/versions/plan`, {
    method: 'POST',
    body: JSON.stringify({ files: declared, replace })
  });
}

export const commitVersion = (datasetId: string, deletes: string[]) =>
  api<{ package_version: number; removed: number }>(`/datasets/${datasetId}/versions/commit`, {
    method: 'POST',
    body: JSON.stringify({ deletes })
  });

const resumeKey = (fileId: string) => `miraxis.upload.${fileId}`;

function loadResume(fileId: string): CompletedPart[] {
  try {
    return JSON.parse(localStorage.getItem(resumeKey(fileId)) || '[]') as CompletedPart[];
  } catch {
    return [];
  }
}

function saveResume(fileId: string, parts: CompletedPart[]) {
  localStorage.setItem(resumeKey(fileId), JSON.stringify(parts));
}

/** An upload that failed for a reason worth showing: the status and what the store said about it. */
export class PartError extends Error {
  constructor(
    public partNumber: number,
    public status: number | null,
    public detail: string
  ) {
    super(`Part ${partNumber} failed${status ? ` (${status}${detail ? ` ${detail}` : ''})` : `: ${detail}`}.`);
    this.name = 'PartError';
  }
}

/**
 * Sleep, unless the upload is cancelled first.
 *
 * A backoff that ignores the signal is a cancel button that appears to do nothing for several
 * seconds, which is worse than not having one.
 */
function delay(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Upload aborted', 'AbortError'));
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Upload aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * The store's own words about why it refused.
 *
 * S3 and MinIO both answer with an XML body naming the code — `SlowDown`, `RequestTimeout`,
 * `InternalError`. Throwing away the body is why a 503 here was a mystery for an hour rather than a
 * fact: `Part 14 failed (503)` says nothing that `Part 14 failed (503 SlowDown)` would not have
 * settled on sight. Read defensively; a diagnostic must never be the thing that throws.
 */
async function storeErrorCode(response: Response): Promise<string> {
  try {
    const body = await response.text();
    return /<Code>([^<]+)<\/Code>/.exec(body)?.[1] ?? body.slice(0, 120).replace(/\s+/g, ' ').trim();
  } catch {
    return '';
  }
}

/**
 * PUT one part, retrying while the store is merely busy.
 *
 * There was no retry at all here, and a single 503 on part 14 of 26 threw straight out of the pool,
 * out of `uploadFile`, and out of the panel's loop — abandoning not just the file but every file
 * after it. The failure was transient by definition: the same bytes went through on the next
 * attempt. Doing that automatically is the whole fix.
 *
 * Jitter is not decoration. The parts in flight fail together, because whatever refused them
 * refused them for the same reason at the same moment — so a fixed schedule would have them all
 * come back in lockstep and recreate the burst that caused it.
 */
async function putPart(
  url: string,
  body: Blob,
  checksum: string,
  partNumber: number,
  signal?: AbortSignal
): Promise<string> {
  for (let attempt = 1; ; attempt++) {
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'PUT',
        body,
        headers: { 'x-amz-checksum-sha256': checksum },
        signal
      });
    } catch (cause) {
      // A cancelled upload is not a failed one, and must not be retried into.
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      // `fetch` rejects for a dropped connection or a DNS blip, which is exactly the transient case.
      if (attempt >= MAX_PART_ATTEMPTS) throw new PartError(partNumber, null, String(cause));
      await delay(BASE_BACKOFF_MS * 2 ** (attempt - 1) * (0.5 + Math.random()), signal);
      continue;
    }

    if (response.ok) return response.headers.get('etag') || '';

    const detail = await storeErrorCode(response);
    if (!RETRYABLE_STATUS(response.status) || attempt >= MAX_PART_ATTEMPTS)
      throw new PartError(partNumber, response.status, detail);

    // `Retry-After` is the store telling us how long it wants; believe it over our own arithmetic.
    const after = Number(response.headers.get('retry-after'));
    const backoff = Number.isFinite(after) && after > 0 ? after * 1000 : BASE_BACKOFF_MS * 2 ** (attempt - 1);
    await delay(backoff * (0.5 + Math.random()), signal);
  }
}

/** Run `worker` over `items` with at most `limit` in flight. */
async function pooled<T>(items: T[], limit: number, worker: (item: T) => Promise<void>) {
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor++;
      await worker(items[index]);
    }
  });
  await Promise.all(runners);
}

export interface UploadOptions {
  /**
   * Called with this file's completed fraction, and with the parts behind it.
   *
   * The fraction alone cannot say whether a stalled bar is a slow part or a dead upload. A 200 MB
   * file is 13 parts, so progress arrives in 13 steps and the gaps between them are minutes on a
   * domestic uplink -- `fetch` gives no events while a body is in flight, so those gaps are real and
   * cannot be smoothed over. Naming the part being worked on is the honest version of that: the
   * viewer can see the count advance and knows what a pause between counts means.
   */
  onProgress?: (fraction: number, parts?: { completed: number; total: number }) => void;
  signal?: AbortSignal;
  /**
   * Path within the dropped directory, e.g. `sample/meta/info.json`. It is what lets the backend
   * recognise a LeRobot layout and read the dataset's own description of itself -- without it a
   * directory upload arrives as a pile of unrelated files with no structure to read.
   */
  relativePath?: string;
  /** A folder or an archive — the two coherent units intake accepts. */
  sourceKind?: 'directory' | 'archive';
  rootName?: string;
}

/**
 * An archive is a *kind* of upload, not just a file with a suffix.
 *
 * The server unpacks members only for `source_kind: 'archive'`; anything else is considered for a
 * run only when its own name ends `.mcap`. So a `.zip` or `.tar.gz` sent as a plain file was
 * stored, never opened, and the batch failed to submit with `no_mcap_files` — the bytes were there
 * and nothing looked inside them. These are the extensions `archive.ts` can actually open.
 */

/**
 * Whether a batch will still accept bytes.
 *
 * This used to be `!submitted_at && !terminal_outcome` — the two fields the server tested before
 * refusing with "Files cannot be added after evaluation has started". Versions invert that: a
 * submitted batch is exactly the one a vendor needs to upload into, because the verdict has told
 * them what to fix and the correction becomes the next version.
 *
 * Two states still refuse, and both are the server's answer rather than a guess:
 *
 *   `evaluating`        — a run is in flight over the frozen version. Opening a successor mid-run
 *                         would leave the vendor watching a verdict for bytes already replaced.
 *   `decision_required` — an operator owes a verdict on exactly these bytes. Changing them under
 *                         the person reviewing them is the same mistake one step later.
 *   `certified`         — an operator has signed off. That is the end of the line, not a stage.
 *
 * An allow-list would be safer against a future state being silently uploadable, but the honest
 * shape here is a deny-list: every other stage genuinely does accept a correction, including
 * `rejected`, which is the one a vendor most needs to answer.
 */
const FROZEN = ['evaluating', 'decision_required', 'certified'];
export const acceptsUploads = (batch: { lifecycle_status: string }) => !FROZEN.includes(batch.lifecycle_status);

const ARCHIVE = /\.(zip|tar|tar\.gz|tgz)$/i;
export const classifySource = (name: string, relativePath?: string): 'directory' | 'archive' =>
  // Two shapes, not three. `loose` is retired: it was a bucket, and a bucket accumulates -- a
  // corrected file landed *beside* the broken one instead of replacing it. Replacing an attempt is
  // what a version does now, so a file with no path of its own is simply a one-file directory.
  !relativePath && ARCHIVE.test(name) ? 'archive' : 'directory';

export async function uploadFile(
  datasetId: string,
  file: File,
  { onProgress, signal, relativePath, sourceKind, rootName }: UploadOptions = {}
) {
  const init = await api<InitiateResponse>(`/datasets/${datasetId}/uploads/initiate`, {
    method: 'POST',
    body: JSON.stringify({
      original_name: file.name,
      file_size_bytes: file.size,
      content_type: file.type,
      declared_checksum: await hashFile(file),
      relative_path: relativePath ?? null,
      source_kind: sourceKind ?? classifySource(file.name, relativePath),
      root_name: rootName ?? (relativePath ? relativePath.split('/')[0] : null)
    })
  });

  const completed = loadResume(init.dataset_file_id);
  const done = new Set(completed.map(p => p.part_number));
  const report = () =>
    onProgress?.(completed.length / init.total_parts, { completed: completed.length, total: init.total_parts });
  report();

  // Chunk size is server-supplied; never derive it locally (frontend.md section 4.1).
  const slice = (partNumber: number) =>
    file.slice((partNumber - 1) * init.chunk_size, Math.min(file.size, partNumber * init.chunk_size));

  const pending = Array.from({ length: init.total_parts }, (_, i) => i + 1).filter(n => !done.has(n));

  for (let offset = 0; offset < pending.length; offset += PRESIGN_BATCH) {
    if (signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');
    const batch = pending.slice(offset, offset + PRESIGN_BATCH);

    // Every part's hash is declared before its bytes move. That IS the integrity guarantee.
    const checksums = new Map<number, string>();
    for (const partNumber of batch) {
      checksums.set(partNumber, await hashPart(slice(partNumber)));
    }

    const signed = await api<{ urls: Record<number, string> }>(
      `/datasets/${datasetId}/uploads/${init.dataset_file_id}/presign`,
      {
        method: 'POST',
        body: JSON.stringify({
          parts: batch.map(n => ({ part_number: n, checksum_sha256: checksums.get(n) }))
        })
      }
    );

    await pooled(batch, concurrencyFor(file.size), async partNumber => {
      if (signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');
      // Direct to the store - this request does not go through the API proxy, so nothing else is
      // going to retry it on our behalf. An AWS SDK would; a browser `fetch` will not.
      const etag = await putPart(
        signed.urls[partNumber],
        slice(partNumber),
        checksums.get(partNumber)!,
        partNumber,
        signal
      );
      completed.push({ part_number: partNumber, etag });
      saveResume(init.dataset_file_id, completed);
      report();
    });
  }

  completed.sort((a, b) => a.part_number - b.part_number);
  await api(`/datasets/${datasetId}/uploads/${init.dataset_file_id}/complete`, {
    method: 'POST',
    body: JSON.stringify({ parts: completed })
  });
  localStorage.removeItem(resumeKey(init.dataset_file_id));
  return { dataset_file_id: init.dataset_file_id };
}

export async function abortUpload(datasetId: string, fileId: string) {
  await api(`/datasets/${datasetId}/uploads/${fileId}/abort`, { method: 'POST' });
  localStorage.removeItem(resumeKey(fileId));
}
